/**
 * InsightsChart Component
 * Spending by category visualization (Pie & Bar chart)
 */

import React, { useState } from 'react';
import { PieChart, BarChart3 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { formatCurrency } from '@/lib/utils';

export default function InsightsChart({ data = [] }) {
  const { currency: userCurrency } = useAuth();
  const currency = userCurrency || 'USD';
  const [chartType, setChartType] = useState('pie'); // 'pie' or 'bar'

  // Show empty state if no data
  if (data.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.4 }}
        className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-600 rounded-xl p-4 sm:p-6 shadow-sm"
      >
        <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Top 5 Spending by Category
        </h2>
        <p className="text-gray-500 dark:text-gray-400 text-center py-8">
          No spending data yet. Add transactions or complete shopping lists to see your category breakdown.
        </p>
      </motion.div>
    );
  }

  // Sort by spend descending and keep the top five categories for insights
  const displayData = data
    .slice() // avoid mutating caller data
    .sort((a, b) => (b?.amount ?? 0) - (a?.amount ?? 0))
    .slice(0, 5);

  const total = displayData.reduce((sum, item) => sum + item.amount, 0);

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Calculate pie chart segments
  const pieSegments = displayData.map((item, index) => {
    const percentage = (item.amount / total) * 100;
    const previousTotal = displayData.slice(0, index).reduce((sum, i) => sum + i.amount, 0);
    const startAngle = (previousTotal / total) * 360;
    const endAngle = startAngle + (percentage / 100) * 360;
    
    return {
      ...item,
      percentage,
      startAngle,
      endAngle
    };
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.4 }}
      className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-600 rounded-xl p-4 sm:p-6 shadow-sm"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
          Top 5 Spending by Category
        </h2>
        
        {/* Chart Type Toggle */}
        <div className="flex gap-1 sm:gap-2 bg-[#FAFAFA] dark:bg-slate-700 p-1 rounded-xl border-2 border-gray-200">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setChartType('pie')}
            className={`px-2 sm:px-3 py-1.5 rounded-lg transition-all duration-200 touch-manipulation ${
              chartType === 'pie'
                ? 'bg-[#006D77] text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:bg-white dark:hover:bg-slate-600'
            }`}
          >
            <PieChart size={16} className="sm:w-[18px] sm:h-[18px]" />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setChartType('bar')}
            className={`px-2 sm:px-3 py-1.5 rounded-lg transition-all duration-200 touch-manipulation ${
              chartType === 'bar'
                ? 'bg-[#006D77] text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:bg-white dark:hover:bg-slate-600'
            }`}
          >
            <BarChart3 size={16} className="sm:w-[18px] sm:h-[18px]" />
          </motion.button>
        </div>
      </div>

      {/* Chart Display */}
      <AnimatePresence mode="wait">
        {chartType === 'pie' ? (
          <motion.div
            key="pie"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            className="space-y-3 sm:space-y-4"
          >
            {/* Simple Pie Chart Visualization */}
            <div className="flex items-center justify-center mb-4 sm:mb-6">
              <div className="relative w-36 h-36 sm:w-48 sm:h-48">
                <svg viewBox="0 0 100 100" className="transform -rotate-90">
                  {pieSegments.map((segment, index) => {
                    const radius = 40;
                    const circumference = 2 * Math.PI * radius;
                    const offset = circumference - (segment.percentage / 100) * circumference;
                    const rotation = (segment.startAngle * circumference) / 360;

                    return (
                      <motion.circle
                        key={index}
                        initial={{ strokeDashoffset: circumference }}
                        animate={{ strokeDashoffset: offset }}
                        transition={{ duration: 0.8, delay: 0.1 * index }}
                        cx="50"
                        cy="50"
                        r={radius}
                        fill="none"
                        stroke={segment.color}
                        strokeWidth="20"
                        strokeDasharray={circumference}
                        style={{
                          strokeDashoffset: offset,
                          transform: `rotate(${segment.startAngle}deg)`,
                          transformOrigin: '50% 50%'
                        }}
                      />
                    );
                  })}
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="text-lg sm:text-2xl font-bold font-mono text-gray-900 dark:text-white">
                    {formatCurrency(total, currency)}
                  </div>
                  <div className="text-xs sm:text-sm font-normal text-gray-500 dark:text-gray-400">Total</div>
                </div>
              </div>
            </div>

            {/* Legend */}
            <div className="space-y-2">
              {displayData.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.5 + index * 0.05 }}
                  className="flex items-center justify-between p-2 sm:p-3 bg-[#FAFAFA] dark:bg-slate-700 rounded-xl"
                >
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                    <div
                      className="w-3 h-3 sm:w-4 sm:h-4 rounded flex-shrink-0"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="font-normal text-sm sm:text-base text-gray-900 dark:text-white truncate">{item.category}</span>
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                    <span className="font-bold font-mono text-sm sm:text-base text-gray-900 dark:text-white">
                      {formatCurrency(item.amount, currency)}
                    </span>
                    <span className="text-xs sm:text-sm font-semibold text-gray-500 dark:text-gray-400">
                      {((item.amount / total) * 100).toFixed(0)}%
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="bar"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            className="space-y-3 sm:space-y-4"
          >
            {/* Bar Chart */}
            <div className="space-y-2.5 sm:space-y-3">
              {displayData.map((item, index) => {
                const maxAmount = Math.max(...displayData.map(d => d.amount));
                const widthPercentage = (item.amount / maxAmount) * 100;

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: 0.1 * index }}
                    className="space-y-1.5 sm:space-y-2"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-normal text-gray-900 dark:text-white text-xs sm:text-sm truncate flex-1">
                        {item.category}
                      </span>
                      <span className="font-bold font-mono text-gray-900 dark:text-white text-sm sm:text-base flex-shrink-0">
                        {formatCurrency(item.amount, currency)}
                      </span>
                    </div>
                    <div className="h-7 sm:h-8 bg-gray-100 dark:bg-slate-600 rounded-xl overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${widthPercentage}%` }}
                        transition={{ duration: 0.8, delay: 0.2 + index * 0.1 }}
                        className="h-full rounded-xl flex items-center justify-end px-2 sm:px-3"
                        style={{ backgroundColor: item.color }}
                      >
                        <span className="text-[10px] sm:text-xs font-semibold text-white">
                          {((item.amount / total) * 100).toFixed(0)}%
                        </span>
                      </motion.div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
